import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useStore } from '../store/useStore';
import {
  uploadSheets, getSheets, getVersionSets,
  getOcrPageUrl, getSheetPdfUrl,
  updateSheet, publishVersionSet,
  seedProjectDisciplines,
} from '../utils/api';
import { runOcrOnSheet, prewarmOcrEngine, prefetchSheetDocs } from '../utils/ocrEngine';
import toast from 'react-hot-toast';
import styles from './UploadModal.module.css';

const ORIENTATIONS = [
  { key: 'horizontal',    label: 'Horizontal' },
  { key: 'vertical-up',   label: 'Vert ↑' },
  { key: 'vertical-down', label: 'Vert ↓' },
];

const EMPTY_REGIONS = {
  number: { rect: null, orientation: 'horizontal' },
  title:  { rect: null, orientation: 'horizontal' },
};

/*
  Upload wizard (four steps):
    1. pick       — choose files + version set + drawing date (unchanged UX).
                    Submitting uploads the files and transitions to step 2.
    2. ocr-setup  — pick title + sheet-number regions on a sample sheet.
    3. ocr-run    — sequentially OCR each uploaded sheet × each region.
    4. review     — editable list of extracted metadata. Save for later (stays
                    draft) or Publish (flips is_published=true on the set).

  Uploaded sheets belong to a version set that starts as a draft. Until the
  set is published, only admin/pm can see those sheets in the drawings list.

  This commit introduces the step machine + pick step only; other steps are
  placeholder stubs so the scaffolding can ship and be eyeballed before the
  OCR UI lands.
*/

const STEPS = [
  { key: 'pick',      label: '1. FILES' },
  { key: 'ocr-setup', label: '2. OCR REGIONS' },
  { key: 'ocr-run',   label: '3. RUN OCR' },
  { key: 'review',    label: '4. REVIEW' },
];

export default function UploadModal() {
  const {
    setUploadModalOpen, project,
    disciplines, setDisciplines,
    versionSets, setVersionSets,
    sheets, setSheets,
    resumeDraftVersionSetId, clearResumeDraft,
    setDrawingsSettingsOpen,
  } = useStore();

  // If opened via "Resume" from the Version Sets panel, jump straight to the
  // review step against that set's existing sheets.
  const resuming = Boolean(resumeDraftVersionSetId);
  const [step, setStep] = useState(resuming ? 'review' : 'pick');

  // Step 1 (pick) state
  const [files, setFiles]                   = useState([]);
  const [versionSetName, setVersionSetName] = useState('');
  const [versionSetDate, setVersionSetDate] = useState('');
  const [uploading, setUploading]           = useState(false);

  // Carried forward after upload — drive steps 2–4. When resuming a draft,
  // seed from the already-persisted sheets for that set.
  const [createdSheets, setCreatedSheets] = useState(() =>
    resuming
      ? sheets.filter((s) => s.version_set_id === resumeDraftVersionSetId)
      : []
  );
  const [versionSetId, setVersionSetId] = useState(resumeDraftVersionSetId);
  const [regions, setRegions]           = useState(EMPTY_REGIONS);
  const [ocrResults, setOcrResults]     = useState([]); // [{ sheet_id, number, title, error }]

  function closeWizard() {
    // If we already uploaded (post step 1), the sheets exist as drafts on the
    // server. Closing is safe — they'll still be there under Version Sets.
    clearResumeDraft();
    setUploadModalOpen(false);
  }

  async function handleUploadComplete(rows) {
    setCreatedSheets(rows);
    setVersionSetId(rows[0]?.version_set_id || null);
    // Refresh store lists in the background so the drafts show up in the
    // version-sets panel even if the user abandons the wizard mid-way.
    if (project) {
      const [updatedSheets, updatedVs] = await Promise.all([
        getSheets({ project_id: project.id }),
        getVersionSets(project.id),
      ]);
      setSheets(updatedSheets);
      setVersionSets(updatedVs);
    }
    setStep('ocr-setup');
  }

  return (
    <div
      className={styles.backdrop}
      onClick={(e) => { if (e.target === e.currentTarget) closeWizard(); }}
    >
      <div className={`${styles.modal} ${step === 'ocr-setup' ? styles.modalWide : ''}`}>
        {/* Header + step indicator */}
        <div className={styles.header}>
          <span className={styles.title}>
            {step === 'pick' ? 'UPLOAD SHEETS' : 'UPLOAD & OCR'}
          </span>
          <div className={styles.stepRow}>
            {STEPS.map((s, i) => {
              const activeIdx = STEPS.findIndex((x) => x.key === step);
              const done   = i < activeIdx;
              const active = i === activeIdx;
              return (
                <span
                  key={s.key}
                  className={
                    `${styles.stepChip} ${active ? styles.stepChipActive : ''} ${done ? styles.stepChipDone : ''}`
                  }
                >
                  {s.label}
                </span>
              );
            })}
          </div>
          <button className={styles.closeBtn} onClick={closeWizard}>×</button>
        </div>

        {step === 'pick' && (
          <PickFilesStep
            files={files} setFiles={setFiles}
            versionSetName={versionSetName} setVersionSetName={setVersionSetName}
            versionSetDate={versionSetDate} setVersionSetDate={setVersionSetDate}
            uploading={uploading} setUploading={setUploading}
            disciplines={disciplines} setDisciplines={setDisciplines}
            versionSets={versionSets}
            project={project}
            openDrawingsSettings={() => setDrawingsSettingsOpen(true)}
            onCancel={closeWizard}
            onUploaded={handleUploadComplete}
          />
        )}

        {step === 'ocr-setup' && (
          <OcrSetupStep
            createdSheets={createdSheets}
            regions={regions}
            setRegions={setRegions}
            onSaveForLater={closeWizard}
            onNext={() => setStep('ocr-run')}
            onSkip={() => setStep('review')}
          />
        )}

        {step === 'ocr-run' && (
          <OcrRunStep
            createdSheets={createdSheets}
            regions={regions}
            results={ocrResults}
            setResults={setOcrResults}
            onBack={() => setStep('ocr-setup')}
            onNext={() => setStep('review')}
          />
        )}

        {step === 'review' && (
          <ReviewStep
            createdSheets={createdSheets}
            ocrResults={ocrResults}
            versionSetId={versionSetId}
            project={project}
            setSheets={setSheets}
            setVersionSets={setVersionSets}
            onBack={resuming ? null : () => setStep('ocr-run')}
            onClose={closeWizard}
          />
        )}
      </div>
    </div>
  );
}

// ─── Step 1: Pick files ───────────────────────────────────────────────────────
function PickFilesStep({
  files, setFiles,
  versionSetName, setVersionSetName,
  versionSetDate, setVersionSetDate,
  uploading, setUploading,
  disciplines, setDisciplines,
  versionSets, project,
  openDrawingsSettings,
  onCancel, onUploaded,
}) {
  const [dragging, setDragging] = useState(false);
  const [seedingDisciplines, setSeedingDisciplines] = useState(false);
  const fileInputRef = useRef(null);

  async function handleSeedDefaultDisciplines() {
    if (!project || seedingDisciplines) return;
    setSeedingDisciplines(true);
    try {
      const rows = await seedProjectDisciplines(project.id);
      setDisciplines(rows);
      toast.success(`Added ${rows.length} default disciplines`);
    } catch (err) {
      toast.error(err.message || 'Failed to seed disciplines');
    } finally {
      setSeedingDisciplines(false);
    }
  }

  // Auto-fill date from an existing version set the user is re-using.
  const matchedVs = versionSets.find(
    (v) => v.name.toLowerCase() === versionSetName.trim().toLowerCase()
  );
  const isExistingVs = Boolean(matchedVs);
  const effectiveDate = isExistingVs
    ? (matchedVs.issuance_date ? matchedVs.issuance_date.slice(0, 10) : '')
    : versionSetDate;

  const nameValid = versionSetName.trim().length > 0;
  const dateValid = Boolean(effectiveDate);
  const canUpload = files.length > 0 && nameValid && dateValid && !uploading;

  const onDragOver  = useCallback((e) => { e.preventDefault(); setDragging(true); }, []);
  const onDragLeave = useCallback(() => setDragging(false), []);
  const onDrop      = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, []); // eslint-disable-line

  function addFiles(incoming) {
    const pdfs = incoming.filter((f) => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    if (pdfs.length !== incoming.length) toast.error('Only PDF files are accepted');
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.file.name));
      const fresh = pdfs
        .filter((f) => !existing.has(f.name))
        .map((f) => ({
          file: f,
          meta: {
            // Filename-derived fallbacks for the "Skip OCR" path — these are
            // never shown in the pick UI (OCR populates them in step 4), but
            // sent to the server so a skipped upload still has reasonable
            // sheet_number / title values to display before the user edits.
            sheet_number: deriveSheetNumber(f.name),
            title: deriveTitle(f.name),
            discipline_id: '',
          },
        }));
      return [...prev, ...fresh];
    });
  }

  function updateMeta(idx, key, value) {
    setFiles((prev) => prev.map((item, i) =>
      i === idx ? { ...item, meta: { ...item.meta, [key]: value } } : item
    ));
  }

  function removeFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleUpload() {
    if (!canUpload || !project) return;
    setUploading(true);

    const form = new FormData();
    files.forEach(({ file }) => form.append('files', file));
    form.append('project_id', project.id);
    form.append('version_set_name', versionSetName.trim());
    if (!isExistingVs && versionSetDate) {
      form.append('version_set_date', versionSetDate);
    }
    form.append('sheet_meta', JSON.stringify(
      files.map(({ meta }) => ({
        sheet_number:  meta.sheet_number  || undefined,
        title:         meta.title         || undefined,
        discipline_id: meta.discipline_id || undefined,
      }))
    ));

    try {
      const result = await uploadSheets(form);
      const rows = Array.isArray(result) ? result : [];
      toast.success(`${rows.length || files.length} sheet(s) uploaded`);
      await onUploaded(rows);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      {/* Empty-disciplines banner — shown only when this project has no
          disciplines yet. Offers one-click seeding of the standard AEC set
          or a jump into Drawings Settings to manage them by hand. */}
      {disciplines.length === 0 && (
        <div className={styles.noDisciplinesBanner}>
          <div className={styles.noDisciplinesText}>
            <strong>No disciplines yet.</strong>
            <span>
              Add the standard AEC set (G, C, L, A, S, M, E, P, FP, T) so you can
              tag sheets during upload.
            </span>
          </div>
          <div className={styles.noDisciplinesActions}>
            <button
              className={styles.secondaryBtn}
              onClick={openDrawingsSettings}
              disabled={seedingDisciplines}
            >
              Manage…
            </button>
            <button
              className={styles.primaryBtn}
              onClick={handleSeedDefaultDisciplines}
              disabled={seedingDisciplines}
            >
              {seedingDisciplines ? 'Adding…' : 'Add defaults'}
            </button>
          </div>
        </div>
      )}

      {/* Drop zone */}
      <div
        className={`${styles.dropzone} ${dragging ? styles.dragging : ''} ${files.length ? styles.compact : ''}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => !files.length && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          className={styles.hiddenInput}
          onChange={(e) => addFiles(Array.from(e.target.files))}
        />
        {files.length === 0 ? (
          <>
            <UploadIcon />
            <p className={styles.dropText}>
              Drop PDFs here or{' '}
              <button
                className={styles.browseBtn}
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              >
                browse
              </button>
            </p>
            <p className={styles.dropHint}>Multiple files · Max 200 MB each</p>
          </>
        ) : (
          <button
            className={styles.addMoreBtn}
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          >
            + Add more PDFs
          </button>
        )}
      </div>

      {/* Global version set + drawing date */}
      {files.length > 0 && (
        <div className={styles.globalFields}>
          <div className={styles.globalField}>
            <label className={styles.globalLabel}>
              VERSION SET <span className={styles.required}>*</span>
            </label>
            <input
              list="vs-options"
              className={`${styles.vsInput} ${!nameValid ? styles.invalid : ''}`}
              placeholder="e.g. CD-001, IFC Rev A  (creates new if not found)"
              value={versionSetName}
              onChange={(e) => setVersionSetName(e.target.value)}
              required
            />
            <datalist id="vs-options">
              {versionSets.map((vs) => <option key={vs.id} value={vs.name} />)}
            </datalist>
          </div>
          <div className={styles.globalField}>
            <label className={styles.globalLabel}>
              DRAWING DATE <span className={styles.required}>*</span>
            </label>
            <input
              type="date"
              className={`${styles.vsInput} ${!dateValid ? styles.invalid : ''}`}
              value={effectiveDate}
              onChange={(e) => setVersionSetDate(e.target.value)}
              disabled={isExistingVs}
              required
            />
            {isExistingVs && (
              <span className={styles.fieldHint}>
                Using existing date from “{matchedVs.name}”
              </span>
            )}
          </div>
        </div>
      )}

      {/* File rows — sheet # / title come from OCR in step 4, so only
          discipline is editable here. */}
      {files.length > 0 && (
        <div className={styles.fileList}>
          <div className={styles.fileListHeader}>
            <span>File</span>
            <span>Discipline</span>
            <span />
          </div>
          {files.map(({ file, meta }, idx) => (
            <div key={file.name + idx} className={styles.fileRow}>
              <span className={styles.fileName} title={file.name}>
                <PdfIcon />
                {file.name}
              </span>
              <select
                className={styles.metaSelect}
                value={meta.discipline_id}
                onChange={(e) => updateMeta(idx, 'discipline_id', e.target.value)}
              >
                <option value="">— None —</option>
                {disciplines.map((d) => (
                  <option key={d.id} value={d.id}>{d.designator} — {d.name}</option>
                ))}
              </select>
              <button className={styles.removeBtn} onClick={() => removeFile(idx)} title="Remove">×</button>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        <span className={styles.footerNote}>
          {files.length > 0
            ? `${files.length} file${files.length !== 1 ? 's' : ''} queued`
            : 'No files selected'}
        </span>
        <div className={styles.footerActions}>
          <button className={styles.cancelBtn} onClick={onCancel} disabled={uploading}>
            Cancel
          </button>
          <button
            className={styles.uploadBtn}
            onClick={handleUpload}
            disabled={!canUpload}
            title={
              !files.length ? 'Select at least one PDF'
              : !nameValid  ? 'Version set is required'
              : !dateValid  ? 'Drawing date is required'
              : ''
            }
          >
            {uploading
              ? 'Uploading…'
              : `Upload ${files.length > 0 ? files.length : ''} File${files.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Step 2: OCR region picker ────────────────────────────────────────────────
// Two fixed regions (sheet number + title). User picks ONE sample sheet, draws
// both rects with their orientations; the wizard then replays them against every
// uploaded sheet in step 3. Regions live in the parent so switching sample
// sheets keeps the already-drawn rects intact.
const SLOTS = [
  { key: 'number', label: 'SHEET NUMBER', accent: 'var(--green)',  tint: 'var(--green-dim)'  },
  { key: 'title',  label: 'TITLE',        accent: 'var(--purple)', tint: 'var(--purple-dim)' },
];

// Zoomable, pannable sample-sheet preview with sequential sub-steps.
// subStep === 'title'  → user draws the title region
// subStep === 'number' → user draws the sheet-number region
// Both rects stay visible once drawn; orientation picker shows for the
// current sub-step only. Navigation: Title → Number → RUN OCR.
//
// Viewport controls (matches PdfViewer conventions):
// - Mouse wheel      → zoom to cursor
// - Left-drag        → draw rectangle
// - Hold SPACE       → pan mode (cursor turns to grab)
// - Middle-click drag→ pan regardless of mode
// - Zoom controls    → −/+/Fit buttons top-right of the viewport
function OcrSetupStep({ createdSheets, regions, setRegions, onSaveForLater, onNext, onSkip }) {
  const firstId = createdSheets[0]?.id || null;
  const [sampleId, setSampleId] = useState(firstId);
  const [subStep,  setSubStep]  = useState('title'); // 'title' | 'number'

  // Pre-warm the browser-side OCR engine while the user is still drawing
  // their crop regions. createWorker() in tesseract.js downloads the worker
  // script + WASM core + ~22 MB of `eng` traineddata, which adds 5-10 sec to
  // the first OCR run if we wait until they click NEXT. Doing it here on
  // mount means by the time they get to Step 3 the workers are already up.
  useEffect(() => {
    prewarmOcrEngine();
  }, []);

  // Zoom / pan state.
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const [isSpaceDown, setIsSpaceDown] = useState(false);

  // Drawing state (normalized 0–1 coords relative to the image).
  const [dragStart,   setDragStart]   = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);

  // Imperative state for panning — refs so wheel/move handlers don't
  // need re-registration on every state change.
  const isPanning   = useRef(false);
  const panStart    = useRef({ x: 0, y: 0 });
  const panAtStart  = useRef({ x: 0, y: 0 });

  const containerRef = useRef(null); // viewport (overflow:hidden, captures wheel)
  const overlayRef   = useRef(null); // full-image overlay, drives coord math
  const imgRef       = useRef(null); // sample sheet <img>; natural dims drive fit-zoom

  const sampleSheet = createdSheets.find((s) => s.id === sampleId) || createdSheets[0];
  // Bumped from 0.6 → 1.0 (~72 DPI): stays crisp when zoomed in on a title block.
  const pngUrl = sampleSheet ? getOcrPageUrl(sampleSheet.id, 1, 1.0) : null;

  const currentSlot = SLOTS.find((s) => s.key === subStep);
  const currentRect = regions[subStep].rect;

  // Compute the zoom level that fits the image inside the viewport with a small
  // margin, then center it. Called on image load AND when the user clicks "Fit".
  const fitToViewport = useCallback(() => {
    const vp  = containerRef.current;
    const img = imgRef.current;
    if (!vp || !img || !img.naturalWidth || !img.naturalHeight) return;
    const vpW = vp.clientWidth;
    const vpH = vp.clientHeight;
    const fit = Math.min(vpW / img.naturalWidth, vpH / img.naturalHeight) * 0.95;
    setZoom(fit);
    setPan({
      x: (vpW - img.naturalWidth  * fit) / 2,
      y: (vpH - img.naturalHeight * fit) / 2,
    });
  }, []);

  // When the sample sheet changes, clear any in-progress drag. The onLoad
  // handler on <img> will fit-to-viewport once the new image loads.
  useEffect(() => {
    setDragStart(null);
    setDragCurrent(null);
  }, [sampleId]);

  // ── Spacebar hold for pan mode ──
  useEffect(() => {
    function isTypingTarget(e) {
      const t = e.target;
      return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    }
    function down(e) {
      if (e.code === 'Space' && !e.repeat && !isTypingTarget(e)) {
        e.preventDefault();
        setIsSpaceDown(true);
      }
    }
    function up(e) {
      if (e.code === 'Space') setIsSpaceDown(false);
    }
    window.addEventListener('keydown', down);
    window.addEventListener('keyup',   up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup',   up);
    };
  }, []);

  // ── Wheel zoom to cursor (native listener so we can preventDefault) ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onWheel(e) {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const rect   = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setZoom((prevZoom) => {
        const newZoom = Math.min(Math.max(prevZoom * factor, 0.25), 12);
        const actual  = newZoom / prevZoom;
        setPan((p) => ({
          x: cx - (cx - p.x) * actual,
          y: cy - (cy - p.y) * actual,
        }));
        return newZoom;
      });
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ── Mouse interaction ──
  // overlay's getBoundingClientRect() already accounts for the parent's
  // transform (translate + scale), so these 0–1 coordinates stay
  // zoom-independent — no manual math needed.
  function normalize(clientX, clientY) {
    const r = overlayRef.current?.getBoundingClientRect();
    if (!r || r.width === 0 || r.height === 0) return null;
    return {
      x: Math.max(0, Math.min(1, (clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (clientY - r.top)  / r.height)),
    };
  }

  function onPointerDown(e) {
    // Middle-click OR space-held → pan; otherwise draw.
    if (e.button === 1 || isSpaceDown) {
      e.preventDefault();
      isPanning.current  = true;
      panStart.current   = { x: e.clientX, y: e.clientY };
      panAtStart.current = { ...pan };
      containerRef.current?.setPointerCapture?.(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    const p = normalize(e.clientX, e.clientY);
    if (!p) return;
    setDragStart(p);
    setDragCurrent(p);
    overlayRef.current.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e) {
    if (isPanning.current) {
      setPan({
        x: panAtStart.current.x + (e.clientX - panStart.current.x),
        y: panAtStart.current.y + (e.clientY - panStart.current.y),
      });
      return;
    }
    if (!dragStart) return;
    const p = normalize(e.clientX, e.clientY);
    if (p) setDragCurrent(p);
  }
  function onPointerUp(e) {
    if (isPanning.current) {
      isPanning.current = false;
      containerRef.current?.releasePointerCapture?.(e.pointerId);
      return;
    }
    overlayRef.current?.releasePointerCapture?.(e.pointerId);
    if (!dragStart || !dragCurrent) { setDragStart(null); setDragCurrent(null); return; }
    const x = Math.min(dragStart.x, dragCurrent.x);
    const y = Math.min(dragStart.y, dragCurrent.y);
    const w = Math.abs(dragStart.x - dragCurrent.x);
    const h = Math.abs(dragStart.y - dragCurrent.y);
    setDragStart(null);
    setDragCurrent(null);
    if (w < 0.005 || h < 0.005) return; // ignore accidental micro-drags
    setRegions((prev) => ({
      ...prev,
      [subStep]: { ...prev[subStep], rect: { x, y, w, h } },
    }));
  }

  const liveRect = dragStart && dragCurrent
    ? {
        x: Math.min(dragStart.x, dragCurrent.x),
        y: Math.min(dragStart.y, dragCurrent.y),
        w: Math.abs(dragStart.x - dragCurrent.x),
        h: Math.abs(dragStart.y - dragCurrent.y),
      }
    : null;

  // ── Actions ──
  function setOrientation(orientation) {
    setRegions((prev) => ({ ...prev, [subStep]: { ...prev[subStep], orientation } }));
  }
  function clearCurrentRegion() {
    setRegions((prev) => ({
      ...prev,
      [subStep]: { rect: null, orientation: prev[subStep].orientation },
    }));
  }
  function zoomIn()  { setZoom((z) => Math.min(z * 1.3, 12)); }
  function zoomOut() { setZoom((z) => Math.max(z / 1.3, 0.1)); }
  function zoomFit() { fitToViewport(); }

  const canAdvance = Boolean(currentRect);
  const cursor = isPanning.current
    ? 'grabbing'
    : isSpaceDown
      ? 'grab'
      : 'crosshair';

  return (
    <>
      {/* Sub-step header + sample sheet picker */}
      <div className={styles.ocrSetupBar}>
        <div className={styles.ocrSetupBarLeft}>
          <span className={styles.ocrSubStepTag} style={{ background: currentSlot.accent }}>
            STEP 2{subStep === 'title' ? 'A' : 'B'}
          </span>
          <span className={styles.ocrSetupPrompt}>
            Draw the{' '}
            <strong style={{ color: currentSlot.accent }}>
              {currentSlot.label}
            </strong>
            {' '}region on the sample sheet.
          </span>
        </div>
        <div className={styles.ocrSetupBarRight}>
          <label className={styles.ocrSampleLabel}>SAMPLE</label>
          <select
            className={styles.metaSelect}
            value={sampleSheet?.id || ''}
            onChange={(e) => setSampleId(e.target.value)}
          >
            {createdSheets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sheet_number || '(no #)'} — {s.title || s.filename}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Viewport — zoom + pan + draw */}
      <div className={styles.ocrViewport} ref={containerRef} style={{ cursor }}>
        {!pngUrl && <div className={styles.ocrPreviewLoading}>Loading preview…</div>}
        {pngUrl && (
          <div
            className={styles.ocrViewportInner}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
            }}
          >
            <img
              ref={imgRef}
              src={pngUrl}
              alt="Sample sheet preview"
              className={styles.ocrSampleImg}
              draggable={false}
              onLoad={fitToViewport}
            />
            <div
              ref={overlayRef}
              className={styles.ocrDrawLayer}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              {/* Both rects stay visible once drawn */}
              {SLOTS.map((slot) => {
                const r = regions[slot.key].rect;
                if (!r) return null;
                const isCurrent = slot.key === subStep;
                return (
                  <div
                    key={slot.key}
                    className={styles.ocrRegionRect}
                    style={{
                      left:   `${r.x * 100}%`,
                      top:    `${r.y * 100}%`,
                      width:  `${r.w * 100}%`,
                      height: `${r.h * 100}%`,
                      borderColor:     slot.accent,
                      backgroundColor: slot.tint,
                      opacity:         isCurrent ? 1 : 0.5,
                      borderWidth:     `${(isCurrent ? 2 : 1.5) / zoom}px`,
                    }}
                  >
                    <span
                      className={styles.ocrRegionTag}
                      style={{
                        background: slot.accent,
                        fontSize:   `${10 / zoom}px`,
                        padding:    `${2 / zoom}px ${6 / zoom}px`,
                        top:        `-${20 / zoom}px`,
                      }}
                    >
                      {slot.label}
                    </span>
                  </div>
                );
              })}
              {liveRect && (
                <div
                  className={styles.ocrLiveRect}
                  style={{
                    left:        `${liveRect.x * 100}%`,
                    top:         `${liveRect.y * 100}%`,
                    width:       `${liveRect.w * 100}%`,
                    height:      `${liveRect.h * 100}%`,
                    borderColor: currentSlot.accent,
                    backgroundColor: currentSlot.tint,
                    borderWidth: `${2 / zoom}px`,
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Floating zoom controls */}
        <div className={styles.ocrZoomPanel}>
          <button className={styles.ocrZoomBtn} onClick={zoomOut} title="Zoom out">−</button>
          <span className={styles.ocrZoomLabel}>{Math.round(zoom * 100)}%</span>
          <button className={styles.ocrZoomBtn} onClick={zoomIn}  title="Zoom in">+</button>
          <button className={styles.ocrZoomBtn} onClick={zoomFit} title="Reset zoom">Fit</button>
        </div>

        {/* Hint strip */}
        <div className={styles.ocrViewportHint}>
          {isSpaceDown
            ? 'PANNING — release space to draw'
            : 'Click and drag to draw · Hold SPACE to pan · Scroll to zoom'}
        </div>
      </div>

      {/* Orientation + status for the current sub-step */}
      <div
        className={styles.ocrSetupControlBar}
        style={{ borderTopColor: currentSlot.accent }}
      >
        <div className={styles.ocrSetupControlLeft}>
          <span
            className={styles.ocrSlotSwatch}
            style={{ background: currentSlot.accent }}
          />
          <span className={styles.ocrSlotLabel}>{currentSlot.label}</span>
          <span className={styles.ocrSlotStatus}>
            {currentRect
              ? `${Math.round(currentRect.w * 100)}% × ${Math.round(currentRect.h * 100)}%`
              : 'not drawn'}
          </span>
          {currentRect && (
            <button className={styles.ocrClearBtn} onClick={clearCurrentRegion}>
              Clear
            </button>
          )}
        </div>
        <div className={styles.ocrSetupControlRight}>
          <span className={styles.ocrOrientLabel}>ORIENTATION</span>
          <div className={styles.ocrOrientRow}>
            {ORIENTATIONS.map((o) => (
              <button
                key={o.key}
                className={`${styles.ocrOrientBtn} ${regions[subStep].orientation === o.key ? styles.ocrOrientActive : ''}`}
                onClick={() => setOrientation(o.key)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer navigation */}
      <div className={styles.footer}>
        <button className={styles.cancelBtn} onClick={onSaveForLater}>
          SAVE FOR LATER
        </button>
        <div className={styles.footerActions}>
          {subStep === 'title' ? (
            <button className={styles.cancelBtn} onClick={onSkip}>
              Skip OCR
            </button>
          ) : (
            <button className={styles.cancelBtn} onClick={() => setSubStep('title')}>
              ← Back to Title
            </button>
          )}
          <button
            className={styles.uploadBtn}
            onClick={() => (subStep === 'title' ? setSubStep('number') : onNext())}
            disabled={!canAdvance}
            title={canAdvance ? '' : `Draw the ${currentSlot.label.toLowerCase()} region to continue`}
          >
            {subStep === 'title' ? 'NEXT: SHEET NUMBER →' : 'RUN OCR →'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Step 3: Run OCR across every uploaded sheet ─────────────────────────────
// Worker-pool over the uploaded sheets — each worker pulls the next sheet from
// a shared index, runs both region OCRs (number + title) on it, and pushes the
// result. Errors are captured per sheet and don't abort the whole run.
//
// CONCURRENCY tuning: OCR runs in the browser via utils/ocrEngine. Sweet
// spot determined empirically — pushing to 16 caused per-render to jump
// 4× (~2.5s → ~10s) due to CPU contention between concurrent pdf.js
// renders, AND server egress contention spiked per-fetch from ~5s → ~20s.
// 8 keeps all the parallel pieces happy without overrunning either CPU or
// network. Combined with prefetchSheetDocs(_, 4) below, fetch happens
// ~1 sheet ahead of where the OCR loop is, so workers don't sit idle
// waiting on network.
const OCR_CONCURRENCY = 8;

function OcrRunStep({ createdSheets, regions, results, setResults, onBack, onNext }) {
  const [running, setRunning] = useState(false);
  const [progressIdx, setProgressIdx] = useState(0); // highest sheet picked up across all workers; meaningful as "we're at ~N of TOTAL"
  const cancelRef = useRef(false);
  const startedRef = useRef(false);

  const total = createdSheets.length;
  const done = results.length;
  const errorCount = results.filter((r) => r.error || r.number?.error || r.title?.error).length;

  useEffect(() => {
    // Guard against double-run under StrictMode / re-renders
    if (startedRef.current) return;
    if (results.length >= total) return; // already finished
    startedRef.current = true;
    cancelRef.current  = false;

    (async () => {
      setRunning(true);

      // Skip sheets we've already processed (allows resume if user hit back/next)
      const todo = createdSheets.filter(
        (s) => !results.find((r) => r.sheet_id === s.id)
      );

      // Kick off background fetches for every sheet in the todo list as soon
      // as the run starts — fetch+parse is the dominant phase per the
      // ocrEngine diagnostic logs, and overlapping all fetches with the
      // render+OCR work hides that latency. Fire-and-forget; the worker
      // loop's runOcrOnSheet awaits the same cached promises.
      prefetchSheetDocs(todo.map((s) => s.id));

      let nextIdx = 0;
      const collected = [...results];

      async function worker() {
        while (true) {
          if (cancelRef.current) return;
          const idx = nextIdx++;
          if (idx >= todo.length) return;
          const sheet = todo[idx];
          setProgressIdx((prev) => Math.max(prev, idx + 1));

          const entry = {
            sheet_id: sheet.id,
            filename: sheet.filename,
            sheet_number_current: sheet.sheet_number,
            title_current: sheet.title,
          };
          // Run OCR in the browser via utils/ocrEngine. Loads the sheet PDF
          // once (cached in-engine for the duration of the sheet), renders +
          // OCRs each region against the shared Tesseract worker pool, then
          // releases the doc. `region` per item picks the Tesseract tuning
          // profile inside the engine (single-line + charset whitelist for
          // numbers, uniform block for titles).
          try {
            const { results: regs } = await runOcrOnSheet(sheet.id, 1, ['number', 'title'].map((slot) => ({
              crop_rect:   regions[slot].rect,
              orientation: regions[slot].orientation,
              region:      slot,
            })));
            for (const r of regs) {
              entry[r.region] = {
                text: (r.text || '').trim(),
                raw_text: r.raw_text || null,
                corrected_from: r.corrected_from || null,
                confidence: r.confidence,
                ms: r.ms,
                // Pre-rotation crop image data URL — surfaced in REVIEW so
                // the user can spot-check what OCR was looking at without
                // having to open the full sheet.
                crop_dataurl: r.crop_dataurl || null,
              };
            }
          } catch (err) {
            // Whole-sheet failure — mark both slots so the UI shows what
            // happened instead of leaving them silently blank.
            for (const slot of ['number', 'title']) {
              entry[slot] = { text: '', confidence: 0, error: err.message || 'OCR failed' };
            }
          }
          if (cancelRef.current) return;
          collected.push(entry);
          setResults([...collected]);
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(OCR_CONCURRENCY, todo.length) }, worker)
      );

      setRunning(false);
    })();

    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allDone = !running && done >= total;
  const currentSheet = running && progressIdx > 0 ? createdSheets[progressIdx - 1] : null;

  return (
    <>
      <div className={styles.ocrRunBody}>
        <div className={styles.ocrRunStatus}>
          <div className={styles.ocrRunLabel}>
            {running
              ? `PROCESSING ${progressIdx} / ${total}`
              : allDone
                ? `DONE — ${total} sheet${total !== 1 ? 's' : ''}${errorCount ? ` · ${errorCount} error${errorCount !== 1 ? 's' : ''}` : ''}`
                : `READY — ${done} / ${total}`}
          </div>
          <div className={styles.ocrProgressTrack}>
            <div
              className={styles.ocrProgressFill}
              style={{ width: `${total ? (done / total) * 100 : 0}%` }}
            />
          </div>
          {currentSheet && (
            <div className={styles.ocrRunCurrent} title={currentSheet.filename}>
              {currentSheet.filename}
            </div>
          )}
        </div>

        <div className={styles.ocrRunTable}>
          <div className={styles.ocrRunTableHeader}>
            <span>Sheet</span>
            <span>Number</span>
            <span>Title</span>
          </div>
          {createdSheets.map((sheet) => {
            const r = results.find((x) => x.sheet_id === sheet.id);
            return (
              <div key={sheet.id} className={styles.ocrRunRow}>
                <span className={styles.ocrRunSheet} title={sheet.filename}>
                  {sheet.sheet_number || sheet.filename}
                </span>
                <span className={styles.ocrRunCell}>
                  {r?.number?.error
                    ? <em className={styles.ocrRunError}>{r.number.error}</em>
                    : r?.number
                      ? <>
                          <span className={styles.ocrRunText}>{r.number.text || '(empty)'}</span>
                          <span className={styles.ocrRunConf}>{Math.round(r.number.confidence || 0)}%</span>
                        </>
                      : <span className={styles.ocrRunPending}>—</span>}
                </span>
                <span className={styles.ocrRunCell}>
                  {r?.title?.error
                    ? <em className={styles.ocrRunError}>{r.title.error}</em>
                    : r?.title
                      ? <>
                          <span className={styles.ocrRunText}>{r.title.text || '(empty)'}</span>
                          <span className={styles.ocrRunConf}>{Math.round(r.title.confidence || 0)}%</span>
                        </>
                      : <span className={styles.ocrRunPending}>—</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.footer}>
        <button
          className={styles.cancelBtn}
          onClick={() => { cancelRef.current = true; onBack(); }}
        >
          {running ? 'STOP & BACK' : '← BACK'}
        </button>
        <div className={styles.footerActions}>
          <button
            className={styles.uploadBtn}
            onClick={onNext}
            disabled={running}
            title={running ? 'Wait for OCR to finish' : ''}
          >
            {running ? `Running… (${done}/${total})` : 'REVIEW →'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Step 4: Review extracted metadata, publish or save for later ─────────────
// Pre-fills each row with the OCR output (or the sheet's existing metadata if
// OCR was skipped / failed). User can fix typos inline; edits PATCH on blur.
// Publish flips the version set to is_published=true; Save for later closes
// the wizard and leaves the set as a draft.
function ReviewStep({
  createdSheets, ocrResults, versionSetId,
  project, setSheets, setVersionSets,
  onBack, onClose,
}) {
  // Seed the editable rows once. OCR results override the sheet defaults only
  // when the OCR text is non-empty (avoid wiping a pre-filled sheet number
  // with a blank OCR extraction).
  const [rows, setRows] = useState(() => createdSheets.map((sheet) => {
    const r = ocrResults.find((x) => x.sheet_id === sheet.id) || {};
    const ocrNum   = r.number && !r.number.error ? (r.number.text || '').trim() : '';
    const ocrTitle = r.title  && !r.title.error  ? (r.title.text  || '').trim() : '';
    return {
      sheet_id:     sheet.id,
      filename:     sheet.filename,
      sheet_number: ocrNum   || sheet.sheet_number || '',
      title:        ocrTitle || sheet.title        || '',
      number_conf:  r.number?.confidence ?? null,
      title_conf:   r.title?.confidence  ?? null,
      number_error: r.number?.error || null,
      title_error:  r.title?.error  || null,
      // OCR crop image data URLs — what Tesseract was looking at for each
      // region. Shown as small thumbs in the row so the user can spot-check
      // OCR misreads without opening the full sheet PDF.
      number_thumb: r.number?.crop_dataurl || null,
      title_thumb:  r.title?.crop_dataurl  || null,
      // When the server's discipline-aware post-processing swapped a
      // leading digit to a letter (e.g. "5-101" → "S-101"), stash the raw
      // for a subtle "auto-corrected" badge so the user can spot-check.
      number_corrected_from: r.number?.corrected_from || null,
      status:       'idle', // 'idle' | 'saving' | 'saved' | 'error'
    };
  }));
  const [publishing, setPublishing] = useState(false);

  function updateRow(id, patch) {
    setRows((prev) => prev.map((r) => r.sheet_id === id ? { ...r, ...patch } : r));
  }

  async function saveRow(id) {
    const row = rows.find((r) => r.sheet_id === id);
    if (!row) return;
    updateRow(id, { status: 'saving' });
    try {
      await updateSheet(id, {
        sheet_number: row.sheet_number.trim(),
        title:        row.title.trim(),
      });
      updateRow(id, { status: 'saved' });
    } catch (err) {
      updateRow(id, { status: 'error' });
      toast.error(err.message || 'Save failed');
    }
  }

  async function handlePublish() {
    if (!versionSetId) { toast.error('No version set to publish'); return; }
    setPublishing(true);
    try {
      // Flush any unsaved edits — run in parallel, each saveRow PATCHes one sheet
      await Promise.all(rows.map((r) => saveRow(r.sheet_id)));
      await publishVersionSet(versionSetId);
      toast.success('Version set published');
      if (project) {
        const [updatedSheets, updatedVs] = await Promise.all([
          getSheets({ project_id: project.id }),
          getVersionSets(project.id),
        ]);
        setSheets(updatedSheets);
        setVersionSets(updatedVs);
      }
      onClose();
    } catch (err) {
      toast.error(err.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  }

  async function handleSaveForLater() {
    try {
      await Promise.all(rows.map((r) => saveRow(r.sheet_id)));
      if (project) {
        const updated = await getVersionSets(project.id);
        setVersionSets(updated);
      }
      toast.success('Saved as draft — finish later from Version Sets');
    } catch {
      // Per-row toast already fired
    } finally {
      onClose();
    }
  }

  function confClass(conf) {
    if (conf == null) return '';
    if (conf >= 80) return styles.confHigh;
    if (conf >= 60) return styles.confMed;
    return styles.confLow;
  }

  return (
    <>
      <div className={styles.reviewBody}>
        <p className={styles.reviewIntro}>
          Review the extracted sheet numbers and titles. Low-confidence cells
          are highlighted — fix any typos inline. Edits save on blur.
        </p>

        <div className={styles.reviewTable}>
          <div className={styles.reviewHeader}>
            <span>File</span>
            <span>Sheet #</span>
            <span>Title</span>
            <span className={styles.reviewStatusCol}>Status</span>
          </div>
          {rows.map((row) => (
            <div key={row.sheet_id} className={styles.reviewRow}>
              <span className={styles.reviewFile} title={row.filename}>
                {row.filename}
                <a
                  href={getSheetPdfUrl(row.sheet_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.reviewSheetLink}
                  title="Open the full sheet PDF in a new tab"
                  onClick={(e) => e.stopPropagation()}
                >
                  view ↗
                </a>
              </span>
              <div className={styles.reviewInputWrap}>
                <div className={styles.reviewInputRow}>
                  <input
                    className={`${styles.metaInput} ${confClass(row.number_conf)}`}
                    value={row.sheet_number}
                    onChange={(e) => updateRow(row.sheet_id, { sheet_number: e.target.value, status: 'idle' })}
                    onBlur={() => saveRow(row.sheet_id)}
                    placeholder="—"
                    disabled={publishing}
                  />
                  {row.number_corrected_from && (
                    <span
                      className={styles.reviewCorrected}
                      title={`Auto-corrected from "${row.number_corrected_from}" using the project's discipline list.`}
                    >
                      ✎
                    </span>
                  )}
                  {row.number_conf != null && (
                    <span className={`${styles.reviewConf} ${confClass(row.number_conf)}`}>
                      {Math.round(row.number_conf)}%
                    </span>
                  )}
                </div>
                {row.number_thumb && (
                  <img
                    src={row.number_thumb}
                    alt="OCR crop — sheet number"
                    className={styles.reviewThumb}
                    title="What OCR was looking at for the sheet number"
                  />
                )}
              </div>
              <div className={styles.reviewInputWrap}>
                <div className={styles.reviewInputRow}>
                  <input
                    className={`${styles.metaInput} ${confClass(row.title_conf)}`}
                    value={row.title}
                    onChange={(e) => updateRow(row.sheet_id, { title: e.target.value, status: 'idle' })}
                    onBlur={() => saveRow(row.sheet_id)}
                    placeholder="—"
                    disabled={publishing}
                  />
                  {row.title_conf != null && (
                    <span className={`${styles.reviewConf} ${confClass(row.title_conf)}`}>
                      {Math.round(row.title_conf)}%
                    </span>
                  )}
                </div>
                {row.title_thumb && (
                  <img
                    src={row.title_thumb}
                    alt="OCR crop — sheet title"
                    className={styles.reviewThumb}
                    title="What OCR was looking at for the sheet title"
                  />
                )}
              </div>
              <span className={styles.reviewStatusCol}>
                {row.status === 'saving' && <span className={styles.reviewDim}>saving…</span>}
                {row.status === 'saved'  && <span className={styles.reviewOk}>✓</span>}
                {row.status === 'error'  && <span className={styles.reviewErr}>!</span>}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        {onBack ? (
          <button className={styles.cancelBtn} onClick={onBack} disabled={publishing}>
            ← BACK
          </button>
        ) : (
          <span className={styles.footerNote}>Draft version set</span>
        )}
        <div className={styles.footerActions}>
          <button
            className={styles.cancelBtn}
            onClick={handleSaveForLater}
            disabled={publishing}
          >
            SAVE FOR LATER
          </button>
          <button
            className={styles.uploadBtn}
            onClick={handlePublish}
            disabled={publishing}
          >
            {publishing ? 'Publishing…' : 'PUBLISH'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Placeholder step (unused once all four steps ship) ───────────────────────
function PlaceholderStep({ title, hint, onBack, onNext, nextLabel, backLabel }) {
  return (
    <>
      <div className={styles.placeholderBody}>
        <h3 className={styles.placeholderTitle}>{title}</h3>
        <p className={styles.placeholderHint}>{hint}</p>
      </div>
      <div className={styles.footer}>
        <span className={styles.footerNote}>Step in progress — UI coming in next commit</span>
        <div className={styles.footerActions}>
          <button className={styles.cancelBtn} onClick={onBack}>{backLabel}</button>
          <button className={styles.uploadBtn} onClick={onNext}>{nextLabel}</button>
        </div>
      </div>
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function deriveSheetNumber(filename) {
  const base  = filename.replace(/\.pdf$/i, '');
  const match = base.match(/^([A-Z]{1,3}[-_]?\d{3,4}[A-Za-z]?)/);
  return match ? match[1].replace('_', '-') : base.slice(0, 10);
}
function deriveTitle(filename) {
  return filename
    .replace(/\.pdf$/i, '')
    .replace(/^[A-Z]{1,3}[-_]?\d{3,4}[A-Za-z]?[-_\s]*/i, '')
    .replace(/[-_]/g, ' ')
    .trim();
}

const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M16 4v16M10 10l6-6 6 6M6 24v4a2 2 0 002 2h16a2 2 0 002-2v-4"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PdfIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <rect x="2" y="1" width="10" height="13" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M5 5h6M5 8h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    <path d="M10 1v4h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
